// Translations
import { useTrans } from "@/i18n"

// GraphQL
import { useChangePasswordMutation } from "@/api/graphql"

// Forms
import {
	FormikProvider,
	useFormik,
	handleErrorWithFormik,
	mapGraphQLErrorsToFormik,
} from "@/lib/formik"
import { FormikError } from "../form-controls/Errors"
import { FormGroup } from "../form-controls/FormGroup"
import {
	FormikErrors,
	FormikInput,
	FormikLabel,
	FormikSubmitButton,
} from "../form-controls/formik"
import { Yup } from "@/lib/forms"
import { LabelSubtitle } from "../form-controls/Label"

// UI
import { Heading } from "../Typography"
import { CardBody, CardFooter, CardWrapper } from "../Card"
import { useToasts } from "@/context/useToasts"

/**
 * ChangePassword
 * @returns
 */
export const ChangePassword = () => {
	const toasts = useToasts()
	const t = useTrans("profile")

	// Mutation
	const changePasswordMutation = useChangePasswordMutation({
		onSuccess: async (response) => {
			const errors = response?.password_reset?.errors ?? []
			const mapped = mapGraphQLErrorsToFormik(errors, form)

			// The backend surfaces "wrong current password" as nonFieldErrors —
			// route it to the errors.local banner rather than a field.
			const wrongPassword = errors.some(
				(e) => e?.field === "nonFieldErrors",
			)
			if (wrongPassword) {
				form.setFieldError(
					"errors.local",
					"profile:change_password.form_errors.wrong_password",
				)
			}

			if (errors.length > 0) {
				if (!mapped && !wrongPassword) {
					form.setFieldError(
						"errors.common",
						"common:form_errors.unknown_error",
					)
				}
				return
			}

			toasts.addToast({
				variant: "success",
				id: "change-password-success",
				text: t(`profile:change_password.success_message`),
			})
		},
		onError: (error: Error) => {
			handleErrorWithFormik(error, form)
		},
	})

	// Form
	const form = useFormik({
		initialValues: {
			password: "",
			newPassword: "",
			newPasswordConfirmation: "",
			errors: { local: "", common: "" },
		},
		validationSchema,
		onSubmit: async (values) => {
			await changePasswordMutation.mutateAsync({
				input: {
					current_password: values?.password ?? "",
					new_password: values?.newPassword ?? "",
					confirm_password: values?.newPasswordConfirmation ?? "",
				},
			})
		},
	})

	return (
		<FormikProvider value={form}>
			<CardWrapper>
				<form onSubmit={form.handleSubmit}>
					<CardBody>
						<Heading
							as="h2"
							styleAs="h5"
							className="mb-3 sm:truncate"
							data-testid={"change-password-heading"}
						>
							{t("profile:change_password.title")}
						</Heading>
						<FormGroup
							heading={<p>{t("profile:change_password.copy")}</p>}
						>
							<div className="space-y-6">
								<div>
									<FormikLabel htmlFor="password">
										{t(
											"profile:change_password.password.title",
										)}
										<LabelSubtitle>
											{t(
												"profile:change_password.password.copy",
											)}
										</LabelSubtitle>
									</FormikLabel>
									<FormikInput
										name="password"
										type="password"
										required
										autoComplete="current-password"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:change_password.password.title",
										)}
										placeholder={t(
											"profile:change_password.password.placeholder",
										)}
									/>
								</div>
								<div>
									<FormikLabel htmlFor="newPassword">
										{t(
											"profile:change_password.new_password.title",
										)}
										<LabelSubtitle>
											{t(
												"profile:change_password.new_password.copy",
											)}
										</LabelSubtitle>
									</FormikLabel>
									<FormikInput
										name="newPassword"
										type="password"
										required
										autoComplete="new-password"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:change_password.new_password.title",
										)}
										placeholder={t(
											"profile:change_password.new_password.placeholder",
										)}
									/>
									<FormikError
										field="newPassword"
										reliesOnField="newPasswordConfirmation"
										namespace="profile"
									/>
								</div>
								<div>
									<FormikLabel htmlFor="newPasswordConfirmation">
										{t(
											"profile:change_password.new_password_confirmation.title",
										)}
									</FormikLabel>
									<FormikInput
										name="newPasswordConfirmation"
										type="password"
										required
										autoComplete="new-password"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:change_password.new_password_confirmation.title",
										)}
										placeholder={t(
											"profile:change_password.new_password_confirmation.placeholder",
										)}
									/>
									<FormikError
										field="newPasswordConfirmation"
										namespace="profile"
									/>
								</div>
								<div>
									<FormikErrors i18nNamespace="profile" />
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<FormikSubmitButton>
							{t("profile:change_password.form_actions.save")}
						</FormikSubmitButton>
					</CardFooter>
				</form>
			</CardWrapper>
		</FormikProvider>
	)
}

const validationSchema = Yup.object().shape({
	password: Yup.string().required(
		"profile:change_password.form_errors.password.required",
	),
	newPassword: Yup.string()
		.min(8, "profile:change_password.form_errors.password.min_8_chars")
		.oneOf(
			[Yup.ref("newPasswordConfirmation")],
			"profile:change_password.form_errors.passwords_do_not_match",
		)
		.required("profile:change_password.form_errors.new_password.required"),
	newPasswordConfirmation: Yup.string()
		.oneOf(
			[Yup.ref("newPassword")],
			"profile:change_password.form_errors.passwords_do_not_match",
		)
		.required(
			"profile:change_password.form_errors.new_password_confirmation.required",
		),
})
